import { motion } from "framer-motion";
import { Heart, BookOpen, Users, Globe } from "lucide-react";

const GO_IMAGE = "https://customer-assets.emergentagent.com/job_dominion-felixstowe/artifacts/unauutwc_image.png";

const values = [
  { icon: Heart, title: "Love", text: "A community bound by the love of Christ" },
  { icon: BookOpen, title: "Faith", text: "Grounded in the Word of God for victorious living" },
  { icon: Users, title: "Community", text: "Building lasting relationships and growing together" },
  { icon: Globe, title: "Mission", text: "Reaching the world with the gospel of Jesus Christ" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.7, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function LeadershipSection() {
  return (
    <section data-testid="leadership-section" className="py-24 px-6 bg-white relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full bg-navy/3 blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image with badge */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative">
              <img
                src={GO_IMAGE}
                alt="Pastor E.A. Adeboye - General Overseer, RCCG"
                className="w-full max-w-md mx-auto rounded-3xl shadow-2xl object-cover"
                data-testid="go-image"
              />
              {/* 70+ Years badge */}
              <motion.div
                className="absolute -bottom-6 right-4 sm:right-8 lg:-right-4 bg-navy rounded-2xl px-6 py-4 shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                data-testid="years-badge"
              >
                <p className="font-heading font-black text-3xl text-white">70+</p>
                <p className="font-body text-white/70 text-sm font-semibold">Years of Faith</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Text content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="font-body text-cgreen font-bold text-sm italic mb-2 block">About Us</span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-6 leading-tight">
              A Place to <span className="text-crimson">Belong</span>
            </h2>
            <p className="font-body text-slate-600 text-base leading-relaxed mb-4">
              The Redeemed Christian Church of God is a vibrant, faith-filled community dedicated to
              the preaching of the gospel. We believe in the power of God's Word to transform lives and
              build champions.
            </p>
            <p className="font-body text-slate-600 text-base leading-relaxed mb-8">
              Whether you're new to faith or a seasoned believer, you'll find a warm and
              welcoming family here at Dominion Parish, Felixstowe.
            </p>

            {/* Value cards */}
            <div className="grid grid-cols-2 gap-4">
              {values.map((val, i) => (
                <motion.div
                  key={val.title}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="p-4 rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
                  data-testid={`value-card-${i}`}
                >
                  <val.icon className="w-6 h-6 text-crimson mb-2" />
                  <p className="font-heading font-bold text-navy text-sm mb-1">{val.title}</p>
                  <p className="font-body text-slate-500 text-xs leading-relaxed">{val.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
